import React, { useMemo, useCallback, useContext, useEffect } from 'react';
import Toggle from '@atlaskit/toggle';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import Lozenge from '@atlaskit/lozenge';
import { useQueryParams } from 'use-query-params';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import Button from '@atlaskit/button/new';
import MediaServicesActualSizeIcon from '@atlaskit/icon/glyph/media-services/actual-size';
import MediaServicesFitToPageIcon from '@atlaskit/icon/glyph/media-services/fit-to-page';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import { OptionType } from '@atlaskit/select';

import {
  flex,
  flexCenter,
  flexStart,
  marginPushRight,
  spaceGapCompact,
  spaceGapCozy,
  spaceGapMinimum,
  spaceTopCozy,
} from '@/styles';
import { filterStore } from '@/controllers';
import { StageDisplayContext, StageDisplayType } from '@/controllers/stage-display-controller';

import { generalBarStyles } from '../../styles';
import AdvancedDropdown from '../../advanced-dropdown';
import { filterBarStyles } from '../styles';
import { sloGroupSearchObjectType } from '../types';

import CohortFilterDropdown from './cohort-filter-dropdown';

type FilterBarDynamicProps = {
  publicId: string;
  cohortTypeSorted: string[];
};

const FilterBarDynamic: React.FC<FilterBarDynamicProps> = ({ publicId, cohortTypeSorted }) => {
  const { search } = useLocation();

  // Find custom cohort filters
  const customFilterOptions: OptionType[] = useMemo(
    () =>
      cohortTypeSorted.slice(1).map(
        (cohort: string) =>
          ({
            label: cohort,
            value: cohort,
          } as OptionType)
      ),
    [cohortTypeSorted]
  );

  const {
    hasBreachStatusOnly,
    hideDrafts,
    cohorts,
    customFilters,
    customFiltersList,
  } = useMemo(() => {
    const parsed: sloGroupSearchObjectType = qs.parse(search);
    // Extract selected cohorts into a Map
    const selectedCohorts: Map<string, string[]> = new Map<string, string[]>();
    cohortTypeSorted.forEach((cohort: string) => {
      const value = parsed[cohort as keyof sloGroupSearchObjectType];
      if (value) {
        selectedCohorts.set(cohort, (value as string).split(','));
      }
    });

    return {
      parsed,
      cohorts: selectedCohorts,
      hasBreachStatusOnly: parsed.hasBreachStatusOnly,
      hideDrafts: parsed.hideDrafts,
      customFilters: parsed.customFilters,
      customFiltersList: (parsed.customFilters || '').split(','),
    };
  }, [search, cohortTypeSorted]);
  const [, setQuery] = useQueryParams({});
  const hasAnyFilterApplied =
    cohorts.size > 0 || hasBreachStatusOnly || hideDrafts || customFilters;
  const { setStageDisplayType, stageDisplayType } = useContext(StageDisplayContext);
  const isFullScreen = stageDisplayType === StageDisplayType.FullScreen;

  // Create an object to reset all selected cohort keys to undefined
  const cohortResets = useMemo(
    () => Object.fromEntries(Array.from(cohorts.keys()).map(key => [key, undefined])),
    [cohorts]
  );

  const handleResetFilters = useCallback(() => {
    setQuery({
      hasBreachStatusOnly: undefined,
      hideDrafts: undefined,
      customFilters: undefined,
      ...cohortResets,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortResets]);

  const handleHasBreachStatusFilterChange = useCallback(() => {
    setQuery({
      hasBreachStatusOnly: hasBreachStatusOnly ? undefined : true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBreachStatusOnly]);

  const handleHideDraftsFilterChange = useCallback(() => {
    setQuery({
      hideDrafts: hideDrafts ? undefined : true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideDrafts]);

  // Set the filter state based on the query params and so we ensure the only
  // source of truth is the URL query params
  const { actions: filterActions } = useContext(filterStore);
  useEffect(() => {
    filterActions.setSLOHasBreachingOnly(hasBreachStatusOnly === 'true');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBreachStatusOnly]);

  const handleToggleFullScreen = useCallback(() => {
    setStageDisplayType(
      stageDisplayType === StageDisplayType.FullScreen
        ? StageDisplayType.Normal
        : StageDisplayType.FullScreen
    );
  }, [setStageDisplayType, stageDisplayType]);

  // Custom filters
  const handleCustomFiltersChange = useCallback(
    (value: string[]) => {
      if (!value.length) {
        setQuery({
          customFilters: undefined,
          ...cohortResets,
        });
        return;
      }
      setQuery({ customFilters: value });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cohortResets]
  );

  // Cohort filter
  const handleCohortChange = useCallback((scope: string, value: string[]) => {
    if (!value.length) {
      setQuery({ [scope]: undefined });
      return;
    }
    setQuery({ [scope]: value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div css={[flexStart, spaceGapCompact, spaceTopCozy, generalBarStyles, filterBarStyles]}>
      <div css={[flexStart, spaceGapCozy]}>
        <FilterIcon size="small" label="filter" />
        <div css={[flexStart, spaceGapMinimum]}>
          <span css={[flexStart, spaceGapMinimum]}>
            Breaching <Lozenge>only</Lozenge>
          </span>
          <Toggle
            onChange={handleHasBreachStatusFilterChange}
            isChecked={hasBreachStatusOnly === 'true'}
          />
        </div>
        <div css={[flexStart, spaceGapMinimum]}>
          <span css={[flexStart, spaceGapMinimum]}>
            Hide <Lozenge appearance="moved">🛠 Draft</Lozenge>
          </span>
          <Toggle onChange={handleHideDraftsFilterChange} isChecked={hideDrafts === 'true'} />
        </div>
      </div>
      <>
        {customFilterOptions.map((option: OptionType) => {
          const cohort: string = option.value as string;
          if (customFiltersList.includes(cohort)) {
            return (
              <CohortFilterDropdown
                key={cohort}
                publicId={publicId}
                value={cohorts.get(cohort) || []}
                cohort={cohort}
                onChange={handleCohortChange}
              />
            );
          }
          return null;
        })}
      </>
      <AdvancedDropdown
        label="Cohort filters"
        onChange={handleCustomFiltersChange}
        value={customFilters ? customFilters.split(',') : undefined}
        options={customFilterOptions}
        isMulti
        staticTrigger
        labelReset="Remove all custom filters"
        appearance="subtle"
        iconAfter={<EditorAddIcon label="" size="small" />}
        rerenderMenuOnSelect
      />

      <div css={[marginPushRight, flex, spaceGapCompact]}>
        <Button appearance="subtle" isDisabled={!hasAnyFilterApplied} onClick={handleResetFilters}>
          Clear filters
        </Button>
        <span css={[flexCenter, spaceGapMinimum]}>
          <Button onClick={handleToggleFullScreen}>
            {isFullScreen ? (
              <MediaServicesFitToPageIcon label="" size="small" />
            ) : (
              <MediaServicesActualSizeIcon label="" size="small" />
            )}
          </Button>
        </span>
      </div>
    </div>
  );
};

export default FilterBarDynamic;
